import React, { useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  useReactFlow,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from '@xyflow/react';
import type { DestinationReturnIdOption } from '../../../ReturnIdDropdown/ReturnIdDropdown.types';
import { CygnusTooltip } from 'reusable-components/CygnusTooltip/CygnusTooltip';
import { K1HierarchyNode } from '../K1HierarchyNode/K1HierarchyNode';
import { K1HierarchyEdge } from '../K1HierarchyEdge/K1HierarchyEdge';
import styles from './K1HierarchyFlowGraph.module.css';
import type { FlowGraphProps } from './K1HierarchyFlowGraph.types';
import { getLayoutedElements } from './K1HierarchyFlowGraph.utils';

const nodeTypes = { k1Node: K1HierarchyNode };
const edgeTypes = { k1RoutedEdge: K1HierarchyEdge };
const proOptions = { hideAttribution: true };

export function K1HierarchyFlowGraph({
  hierarchyData,
  docLookup,
  currentClientId,
  readOnly = false,
  selectedNodeIds,
  onSelectReturnId,
  onToggleSelection,
  onMapActivities,
  isFullscreen,
  onToggleFullscreen,
}: FlowGraphProps) {
  const [locked, setLocked] = useState(true);
  const { fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const cbRef = useRef({ onSelectReturnId, onToggleSelection, onMapActivities });
  cbRef.current = { onSelectReturnId, onToggleSelection, onMapActivities };

  const lastHierarchyKey = useRef('');

  useEffect(() => {
    if (!hierarchyData?.length) return;

    const edgeColor = '#c8cdd5';
    const hierarchyKey = hierarchyData.map((n: any) => n.id).join(',');
    const isStructureChange = hierarchyKey !== lastHierarchyKey.current;
    lastHierarchyKey.current = hierarchyKey;

    const flowNodes: Node[] = hierarchyData.map((node: any) => {
      const docData = docLookup[node.id] ?? null;
      const isK1 = docData?.DocumentType === 'Internal Schedule K-1';
      const docId = String(docData?.DocumentId ?? '');
      const isCurrentClient = node.id === currentClientId;
      return {
        id: `n${node.id}`,
        type: 'k1Node',
        data: {
          partnership: node.name ?? '',
          formType: node.type ?? '',
          taxId: isCurrentClient ? '' : docData?.SourceTaxId || node.tax_id || node.taxId || '',
          taxStatus: docData?.TaxStatus ?? null,
          isCurrentClient,
          isInternalK1: isK1,
          isReadOnly: readOnly,
          selectedReturnId: docData?.InternalK1SelectedReturnId ?? null,
          isSelected: !readOnly && docId ? selectedNodeIds.has(docId) : false,
          docData,
          onSelectReturnId: (rowData: any, option: DestinationReturnIdOption | null) =>
            cbRef.current.onSelectReturnId(rowData, option),
          onToggleSelection: (documentId: string) =>
            cbRef.current.onToggleSelection(documentId),
          onMapActivities: (returnId: any, taxId: any, docId: any, name: any) =>
            cbRef.current.onMapActivities?.(returnId, taxId, docId, name),
        },
        position: { x: 0, y: 0 },
      };
    });

    const flowEdges: Edge[] = hierarchyData.flatMap((node: any) =>
      (node.inputs ?? []).map((inputId: any) => ({
        id: `e${inputId}-${node.id}`,
        source: `n${inputId}`,
        target: `n${node.id}`,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: edgeColor },
        style: { strokeWidth: 1.5, stroke: edgeColor },
      }))
    );

    if (isStructureChange) {
      const { nodes: ln, edges: le } = getLayoutedElements(flowNodes, flowEdges);
      setNodes(ln);
      setEdges(le);
      setTimeout(() => fitView({ padding: 0.15 }), 50);
    } else {
      setNodes((prev) =>
        prev.map((existing) => {
          const updated = flowNodes.find((n) => n.id === existing.id);
          return updated ? { ...existing, data: updated.data } : existing;
        }),
      );
    }
  }, [hierarchyData, docLookup, currentClientId, readOnly, selectedNodeIds, fitView, setEdges, setNodes]);

  const { zoomIn, zoomOut } = useReactFlow();

  return (
    <div className={`${styles.canvas} ${locked ? styles.canvasLocked : ''}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={proOptions}
        zoomActivationKeyCode="Control"
        zoomOnScroll={false}
        panOnScroll={false}
        panOnDrag
        nodesDraggable={!locked}
      />
      <div className={styles.zoomControls}>
        <CygnusTooltip text="Zoom in (Ctrl + scroll)">
          <button className={styles.zoomBtn} onClick={() => zoomIn({ duration: 200 })} aria-label="Zoom in">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </CygnusTooltip>
        <div className={styles.zoomDivider} />
        <CygnusTooltip text="Zoom out (Ctrl + scroll)">
          <button className={styles.zoomBtn} onClick={() => zoomOut({ duration: 200 })} aria-label="Zoom out">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </CygnusTooltip>
        <div className={styles.zoomDivider} />
        <CygnusTooltip text={locked ? 'Unlock - drag moves nodes' : 'Lock - drag pans the canvas, nodes stay fixed'}>
          <button
            className={`${styles.zoomBtn} ${locked ? styles.zoomBtnActive : ''}`}
            onClick={() => setLocked((v) => !v)}
            aria-label={locked ? 'Unlock node dragging' : 'Lock node dragging'}
          >
            {locked ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
              </svg>
            )}
          </button>
        </CygnusTooltip>
        <div className={styles.zoomDivider} />
        <CygnusTooltip text={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
          <button
            className={styles.zoomBtn}
            onClick={onToggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8V5a2 2 0 0 1 2-2h3" /><path d="M16 3h3a2 2 0 0 1 2 2v3" /><path d="M21 16v3a2 2 0 0 1-2 2h-3" /><path d="M8 21H5a2 2 0 0 1-2-2v-3" />
              </svg>
            )}
          </button>
        </CygnusTooltip>
      </div>
    </div>
  );
}
